import { OPR_SESSION_TOKEN } from "@/constants";
import request, { _POST } from "@/services/request";
import { message } from "antd";

const BASE = "/admin/dict";

export type DictEnumType = "string" | "number" | "boolean";

export type DictApplication = {
  id: string;
  appCode: string;
  name: string;
  operator: string;
  description: string;
  status: number;
  enumCount: number;
  createdAt: string;
  updatedAt: string;
};

export type DictEnum = {
  id: string;
  appId: string;
  appCode: string;
  enumKey: string;
  name: string;
  type: DictEnumType;
  description: string;
  operator: string;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
};

export type DictItem = {
  id: string;
  appId: string;
  enumId: string;
  value: string;
  label: string;
  sort: number;
  enabled: boolean;
  remark: string;
  operator: string;
  createdAt: string;
  updatedAt: string;
};

export type DictQueryItem = {
  value: string | number | boolean;
  label: string;
  sort: number;
};

export type ListDictAppsReq = {
  keyword?: string;
  status?: number;
};

export type ListDictAppsRsp = {
  list: DictApplication[];
};

export type CreateDictAppReq = {
  appCode: string;
  name: string;
  description?: string;
};

export type UpdateDictAppReq = {
  appCode: string;
  name?: string;
  description?: string;
  status?: number;
};

export type DeleteDictAppReq = {
  id: string;
};

export type DeleteDictEnumReq = {
  id: string;
};

export type ListDictEnumsReq = {
  app: string;
  keyword?: string;
};

export type ListDictEnumsRsp = {
  list: DictEnum[];
};

export type CreateDictEnumReq = {
  appCode: string;
  enumKey: string;
  name: string;
  type: DictEnumType;
  description?: string;
};

export type UpdateDictEnumReq = {
  appCode: string;
  enumKey: string;
  name: string;
  description?: string;
};

export type ListDictItemsReq = {
  app: string;
  enumKey: string;
  keyword?: string;
};

export type ListDictItemsRsp = {
  list: DictItem[];
};

export type CreateDictItemReq = {
  appCode: string;
  enumId: string;
  value: string;
  label: string;
  sort: number;
  enabled: boolean;
  remark?: string;
};

export type UpdateDictItemReq = {
  appCode: string;
  id: string;
  label: string;
  sort: number;
  enabled: boolean;
  remark?: string;
};

export type QueryDictEnumsReq = {
  app: string;
  keys?: string;
};

export type QueryDictEnumsRsp = {
  enums: Record<string, { items: DictQueryItem[] }>;
};

export type DictImportStrategy = "skip" | "merge" | "overwrite";

export type ExportDictAppReq = {
  appCode: string;
  enumKeys?: string[];
};

export type DictImportSummary = {
  created: number;
  skipped: number;
  merged: number;
  overwritten: number;
  failed: number;
  message?: string;
};

export type ImportDictAppRsp = {
  jobId: string;
  status: string;
  summary: DictImportSummary;
};

function parseContentDispositionFilename(header?: string | null): string | undefined {
  if (!header) return undefined;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const match = header.match(/filename="?([^";\n]+)"?/i);
  return match?.[1]?.trim();
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const listDictApps = (params?: ListDictAppsReq) => {
  return request<ListDictAppsRsp, ListDictAppsReq>(_POST, `${BASE}/apps/list`)(params ?? {});
};

const createDictApp = (params: CreateDictAppReq) => {
  return request<{ app: DictApplication }, CreateDictAppReq>(_POST, `${BASE}/apps/create`)(params);
};

const updateDictApp = (params: UpdateDictAppReq) => {
  return request<{ app: DictApplication }, UpdateDictAppReq>(_POST, `${BASE}/apps/update`)(params);
};

const deleteDictApp = (params: DeleteDictAppReq) => {
  return request<null, DeleteDictAppReq>(_POST, `${BASE}/apps/delete`)(params);
};

const deleteDictEnum = (params: DeleteDictEnumReq) => {
  return request<null, DeleteDictEnumReq>(_POST, `${BASE}/apps/enums/delete`)(params);
};

const listDictEnums = (params: ListDictEnumsReq) => {
  return request<ListDictEnumsRsp, ListDictEnumsReq>(_POST, `${BASE}/apps/enums/list`)(params);
};

const createDictEnum = (params: CreateDictEnumReq) => {
  return request<{ enum: DictEnum }, CreateDictEnumReq>(_POST, `${BASE}/apps/enums/create`)(params);
};

const updateDictEnum = (params: UpdateDictEnumReq) => {
  return request<{ enum: DictEnum }, UpdateDictEnumReq>(_POST, `${BASE}/apps/enums/update`)(params);
};

const listDictItems = (params: ListDictItemsReq) => {
  return request<ListDictItemsRsp, ListDictItemsReq>(_POST, `${BASE}/apps/enum/items/list`)(params);
};

const createDictItem = (params: CreateDictItemReq) => {
  return request<{ item: DictItem }, CreateDictItemReq>(_POST, `${BASE}/apps/enum/items/create`)(params);
};

const updateDictItem = (params: UpdateDictItemReq) => {
  return request<{ item: DictItem }, UpdateDictItemReq>(_POST, `${BASE}/apps/enum/items/update`)(params);
};

const queryDictEnums = (params: QueryDictEnumsReq) => {
  return request<QueryDictEnumsRsp, QueryDictEnumsReq>(_POST, `${BASE}/enums/query`)(params);
};

const exportDictApp = async (params: ExportDictAppReq) => {
  const res = await fetch((process.env.ENV_REPLACE_API_URL || "") + `${BASE}/apps/export`, {
    method: _POST,
    headers: {
      "Content-Type": "application/json",
      Authorization: window.localStorage.getItem(OPR_SESSION_TOKEN) || "",
    },
    body: JSON.stringify(params),
  });

  const disposition = res.headers.get("content-disposition");
  if (!res.ok || !disposition?.includes("attachment")) {
    const text = await res.text();
    let errorMsg = "导出失败";
    try {
      const json = JSON.parse(text) as { msg?: string };
      errorMsg = json.msg ?? errorMsg;
    } catch {
      // ignore parse error
    }
    message.error(errorMsg);
    throw new Error(errorMsg);
  }

  const blob = await res.blob();
  const filename =
    parseContentDispositionFilename(disposition) ?? `${params.appCode}-dict-export.json`;
  downloadBlob(blob, filename);
};

const importDictApp = (file: File, appCode: string, strategy: DictImportStrategy) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("appCode", appCode);
  formData.append("strategy", strategy);

  return request<ImportDictAppRsp, FormData>(_POST, `${BASE}/apps/import`, {
    "Content-Type": "multipart/form-data",
  })(formData);
};

const dictConfigApis = {
  listDictApps,
  createDictApp,
  updateDictApp,
  deleteDictApp,
  deleteDictEnum,
  listDictEnums,
  createDictEnum,
  updateDictEnum,
  listDictItems,
  createDictItem,
  updateDictItem,
  queryDictEnums,
  exportDictApp,
  importDictApp,
};

export default dictConfigApis;
